import {LocalStorageUtils} from '../../infrastructure/utils/local-storage.utils';
import {LOCAL_STORAGE_KEYS} from "../consts";
import {BaseManager} from "../../infrastructure/managers/base-manager";
import {LoggerUtils} from "../../infrastructure/utils/logger/logger-utils";
import {SiteConfig} from "../bootstrap/models/site-config";
import {DeviceStore} from "../app/device.store";
import {singleton} from "../../infrastructure/decorators/singleton";
import {DomUtils} from "../../infrastructure/utils/dom-utils";
import {DynamicCoachmarkDisplayModel} from "../models/dynamic-coachmark/dynamic-coachmark-display-model";
import {DynamicCoachmarkLocalStorageDisplayModel} from "../models/dynamic-coachmark/dynamic-coachmark-local-storage-display-model";
import {DynamicCoachmarkRemoteConfigModel} from "../models/dynamic-coachmark/dynamic-coachmark.remote-config-model";
import {DeviceViewModel} from "../models/device.view-model";
import {TermsUtils} from "../../infrastructure/utils/terms-utils";
import {DYNAMIC_COACHMARK} from "../models/dynamic-coachmark/dynamic-coachmark.consts";

@singleton
export class DynamicCoachmarkDisplayManager extends BaseManager {
  dynamicCoachmarkDisplayCounterDictionary = {};
  validDynamicCoachmarksInSession: Array<DynamicCoachmarkDisplayModel>;
  displayDictionaryKey = LOCAL_STORAGE_KEYS.coachmarkDisplays;

  static injectables = [...BaseManager.injectables, LocalStorageUtils, DeviceViewModel, TermsUtils];

  constructor(
      domUtils: DomUtils,
      logger: LoggerUtils,
      siteConfig: SiteConfig,
      deviceStore: DeviceStore,
      localStorageUtils: LocalStorageUtils,
      deviceViewModel: DeviceViewModel,
      terms: TermsUtils) {
    super(domUtils, logger, siteConfig, deviceStore);

    this.localStorageUtils = localStorageUtils;
    this.device = deviceViewModel;
    this.terms = terms;
  }

  init(remoteConfigModels: Array<Object>) {
    if (remoteConfigModels && remoteConfigModels.length > 0) {
      this.getLocalStorage();
      let dynamicCoachmarkDisplayModels: Array<DynamicCoachmarkDisplayModel> = remoteConfigModels.map((remoteConfig: DynamicCoachmarkRemoteConfigModel) => {
        const remoteConfigModel = new DynamicCoachmarkRemoteConfigModel(remoteConfig)
        if (!this.dynamicCoachmarkDisplayCounterDictionary) {
          this.dynamicCoachmarkDisplayCounterDictionary = {};
        }
        let localStorageModel = this.dynamicCoachmarkDisplayCounterDictionary[remoteConfigModel.id];
        if (!localStorageModel) {
          localStorageModel = new DynamicCoachmarkLocalStorageDisplayModel(remoteConfigModel)
          this.dynamicCoachmarkDisplayCounterDictionary[remoteConfigModel.id] = localStorageModel;
        }
        const shouldRenderCoachmark = this.shouldRenderCoachmark(remoteConfigModel, localStorageModel);
        let displayModel = null;
        if (shouldRenderCoachmark) {
          const xpath = this.device.isSmallView ? remoteConfigModel.xpathMobile : remoteConfigModel.xpathDesktop;
          const placement = this.device.isSmallView ? 'top' : remoteConfigModel.direction;

          displayModel = new DynamicCoachmarkDisplayModel(null, remoteConfigModel, this.terms, this.closeCoachMarks, (id) => this.onVisibleCoachmark(id), xpath, placement);
        }
        return displayModel;
      });
      this.updateLocalStorageModel();

      this.validDynamicCoachmarksInSession = dynamicCoachmarkDisplayModels.removeNulls();

      if (this.validDynamicCoachmarksInSession?.length > 0) {
        this.createBlanket();

        this.waitForElements(this.validDynamicCoachmarksInSession, (targetElement, coachmark) => {
          this.observeElementInViewport(targetElement, coachmark);
        });
      }
    }
  }

  getLocalStorage = () => {
    const currentDisplayLocalStorageObject: Object = this.localStorageUtils.get(this.displayDictionaryKey);
    let localStorageModels = [];
    if (currentDisplayLocalStorageObject) {
      this.dynamicCoachmarkDisplayCounterDictionary = currentDisplayLocalStorageObject;

      for (const key in this.dynamicCoachmarkDisplayCounterDictionary) {
        if (Object.prototype.hasOwnProperty.call(this.dynamicCoachmarkDisplayCounterDictionary, key)) {
          this.dynamicCoachmarkDisplayCounterDictionary[key].currentSessionNum += 1;
        }
      }

      this.updateLocalStorageModel();

    }

    return localStorageModels;
  }

  updateLocalStorageModel = () => {
    this.localStorageUtils.set(this.displayDictionaryKey, this.dynamicCoachmarkDisplayCounterDictionary);
  }


  waitForElements = (selectors, callback, interval = 250, timeout = 60000) => {
    const startTime = Date.now();
    const remainingSelectors = new Set(selectors); // Track remaining selectors

    const checkExist = setInterval(() => {
      remainingSelectors.forEach((selector) => {
        const targetElement = document.evaluate(selector.xpath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
        const isElementValid = targetElement && targetElement.offsetWidth > 0 && targetElement.offsetHeight > 0 && !targetElement.className?.includes('placeholder');
        if (isElementValid) {
          selector.element = targetElement;
          remainingSelectors.delete(selector); // Remove found element from the set
          callback(targetElement, selector); // Call the callback with the found element
        }
      });

      // Stop checking if all selectors are found or timeout is reached
      if (remainingSelectors.size === 0 || Date.now() - startTime > timeout) {
        clearInterval(checkExist);
        if (remainingSelectors.size > 0) {
          console.warn(`Timeout waiting for elements: ${Array.from(remainingSelectors).join(", ")}`);
        }
      }
    }, interval);
  }

// Function to observe an element and log when it's in view
  observeElementInViewport = (element, coachmark) => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio === 1) {
          let shouldRenderBlanket = true;
          if (coachmark) {
            coachmark.timeoutId = setTimeout(() => {
              coachmark.tippy.show();
              if (shouldRenderBlanket) {
                this.showBlanket();
                this.disableScroll();
              }
              observer.unobserve(entry.target);
            }, coachmark.model.delay);
          }

          // Add any additional logic here for when the element is in view

        } else if(coachmark.timeoutId > 0) {
          clearTimeout(coachmark.timeoutId);
        }
      });
    }, { threshold: 1.0 }
    );

    observer.observe(element);
  }

  closeCoachMarks = () => {
    this.hideBlanket();
    this.enableScroll();
  }

  // Helper methods for managing the blanket overlay
  createBlanket = () => {
    this.blanketElement = document.createElement('div');
    this.blanketElement.classList.add('tippy-blanket');
    document.body.appendChild(this.blanketElement);
  };

  showBlanket = () => {
    if (this.blanketElement) {
      this.blanketElement.style.display = 'block';
    }
  };

  hideBlanket = () => {
    if (this.blanketElement) {
      this.blanketElement.style.display = 'none';
    }
  };

  // Method to disable scroll
  disableScroll = () => {
    if (!this.scrollDisabled) {
      this.scrollDisabled = true;
      document.body.style.overflow = 'hidden';
    }
  };

  // Method to enable scroll
  enableScroll = () => {
    if (this.scrollDisabled) {
      this.scrollDisabled = false;
      document.body.style.overflow = '';
    }
  };

  shouldRenderCoachmark = (model: DynamicCoachmarkRemoteConfigModel, localStorageModel: DynamicCoachmarkLocalStorageDisplayModel) => {
    let shouldRenderCoachmark = model.display === true;
    if (shouldRenderCoachmark) {
      let isValidBySessionNum = localStorageModel.currentSessionNum >= model.displayInSessionNum;
      let isValidByDisplayCount = shouldRenderCoachmark && model.displayCount > localStorageModel.visibleCounter;
      shouldRenderCoachmark = isValidBySessionNum && isValidByDisplayCount;
    }

    return shouldRenderCoachmark;
  }

  onVisibleCoachmark(dynamicCoachmarkId: String) {
    const dynamicCoachmarkModel = this.dynamicCoachmarkDisplayCounterDictionary[dynamicCoachmarkId];
    if (dynamicCoachmarkModel) {
      dynamicCoachmarkModel.visibleCounter++;

      this.updateLocalStorageModel();
    }

  }

  getElementXPath = (elementPath) => {
    try {
      // Query the element using the provided path
      let element = document.querySelector(elementPath);

      if (!element) {
        // If no element is found, return a descriptive error message
        return `Error: No element found for the selector '${elementPath}'.`;
      } else {
        // If the element is found, generate the XPath
        const paths = [];
        while (element !== null) {
          let name = element.localName;
          if (!name) break;
          name = name.toLowerCase();

          // Count the number of siblings with the same name
          let index = 1;
          let sibling = element.previousElementSibling;
          while (sibling) {
            if (sibling.localName === element.localName) index++;
            sibling = sibling.previousElementSibling;
          }

          // Add id to the XPath if the element has an id
          let id = element.id ? `[@id="${element.id}"]` : '';

          // Build the XPath and insert it at the beginning of the array
          paths.unshift(`${name}${id}[${index}]`);

          // Move to the parent node
          element = element.parentNode;
        }

        // Return the complete XPath if we successfully generated it
        return paths.length ? '/' + paths.join('/') : 'Error: Unable to generate XPath.';
      }
    } catch (error) {
      return `Error: No element found for the selector '${elementPath}'.`;
    }
  }

  createAndDisplayCoachmark = (elementPath: string) => {
    // Get the XPath or error message
    const xpathResult = this.getElementXPath(elementPath);

    // If the XPath is valid, proceed with displaying the coachmark
    if (xpathResult && !xpathResult.startsWith("Error:")) {
      const xpath = xpathResult;
      const targetElement = document.evaluate(xpath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;

      if (targetElement) {
        const placement = this.device.isSmallView ? 'top' : 'right';
        const remoteConfig = new DynamicCoachmarkRemoteConfigModel(DYNAMIC_COACHMARK.TEST_COACHMARK_MODEL);
        const displayModel = new DynamicCoachmarkDisplayModel(targetElement, remoteConfig, this.terms, this.closeCoachMarks, (id) => this.onVisibleCoachmark(id), xpath, placement);

        displayModel.tippy.show();
        this.showBlanket();

        // Return confirmation message
        return `Coachmark is valid, copy XPath: ${xpath}`;
      } else {
        return "Error: Element not found from XPath.";
      }
    } else {
      // If there's an error in XPath, return the error message
      return xpathResult;
    }
  }

}